import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { FALLBACK_LIMITS, useConfig } from '@entities/config/api';
import { useConfirmSearch, useGeoExpansion } from '@entities/search-run';
import { useSearchSession, useSendChatMessage } from '@entities/search-session';
import { ROUTES } from '@shared/config/routes';
import { randomUuid } from '@shared/lib/uuid';
import { Button, Skeleton } from '@shared/ui';

import { isNetworkError } from '../model/errors';
import { useChatDraft } from '../model/useChatDraft';

import { ChatPanel } from './ChatPanel';
import styles from './KarlDialogue.module.css';

interface KarlDialogueProps {
  searchId: string;
}

/**
 * Диалог с Karl внутри рельса — на всех экранах **после** шага 1 (FE-193).
 *
 * Зачем он там. Уточнить запрос можно в любой момент, включая уже собранную
 * четвёрку (MVP_PRD §35.4), и раньше для этого приходилось уходить со
 * страницы на отдельный экран диалога — то есть терять из виду то, что
 * уточняешь. Диалог переехал в рельс, а кнопки «вернуться к диалогу» с этих
 * экранов убраны: ходить больше некуда.
 *
 * **Само сообщение подбор не пересобирает, но и не молчит.** Как только правка
 * критериев требует нового прогона, под лентой появляется вопрос Karl
 * «Пересобрать подбор?» с кнопкой. Молча пересчитывать нельзя: перестройка
 * освобождает четвёрку, снимает выбор автомобилей и обнуляет счётчики замен —
 * это решение человека. Прятать вопрос в неприметную кнопку внизу тоже нельзя:
 * именно так правка выглядела как ничего не делающая.
 *
 * Признак приходит с сервера (`rebuild_pending`) и переживает перезагрузку:
 * выводить его из `status` и `stage` на фронте значит завести второе правило,
 * которое рано или поздно разойдётся с первым (BE-355).
 *
 * **Перестройка не стоит денег.** Это тот же заказ и тот же диалог: пейволл
 * оплаченному заказу второй раз не показывается, он сам доходит до новой
 * четвёрки, а четвёрка прошлого прогона освобождается с причиной «критерии
 * изменились» (§34.13, MVP_PRD §35.4).
 *
 * Собственных запросов компонент не добавляет: `useSearchSession` берёт тот же
 * ключ TanStack Query, что и страница вокруг.
 */
export function KarlDialogue({ searchId }: KarlDialogueProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: config } = useConfig();
  const maxLength = (config?.limits ?? FALLBACK_LIMITS).chat_message_max_length;

  const sessionQuery = useSearchSession(searchId || null);
  const state = sessionQuery.data ?? null;
  const sessionId = state?.session.id ?? null;

  const sendMessage = useSendChatMessage(sessionId);
  const confirmSearch = useConfirmSearch();
  const geoExpansion = useGeoExpansion();
  const draft = useChatDraft(sessionId);
  const geoOffer = state?.geo_expansion_offer ?? null;

  const send = useCallback(
    (content: string, quickReplyCodes?: string[]) => {
      const clientMessageId = randomUuid();
      draft.clear();
      sendMessage.mutate(
        { clientMessageId, content, quickReplyCodes },
        {
          // Сообщение не дошло: набранный текст возвращается в поле, а не
          // пропадает вместе с откатом реплики. Подсказки тоже были текстом
          // поля (FE-595), и вместе с ним возвращаются нажатыми.
          onError: (error) => {
            if (isNetworkError(error)) draft.restore(content);
          },
        },
      );
    },
    [draft, sendMessage],
  );

  /**
   * Куда уводит подтверждённая перестройка.
   *
   * Оставлять пользователя на прежнем экране нельзя: там результаты прошлого
   * прогона, а новый уже считается. Перестройка ведёт **туда же, куда первый
   * запуск подбора** (FE-595): экран результатов с прогоном в адресе, где идут
   * реальные этапы worker'а. Два разных экрана ожидания для одного и того же
   * действия читались как два разных действия. Оттуда оплаченный заказ сам
   * возвращается на новую четвёрку, неоплаченный остаётся на рабочем наборе.
   */
  const openRun = useCallback(
    (runId: string) => {
      if (!sessionId) return;
      void navigate(`${ROUTES.searchResults(sessionId)}?run=${encodeURIComponent(runId)}`);
    },
    [navigate, sessionId],
  );

  const rebuild = useCallback(() => {
    const criteriaVersionId = state?.criteria.version_id;
    if (!sessionId || criteriaVersionId === undefined) return;
    confirmSearch.mutate(
      { sessionId, criteriaVersionId },
      {
        onSuccess: (accepted) => {
          openRun(accepted.search_run_id);
        },
      },
    );
  }, [confirmSearch, openRun, sessionId, state]);

  /**
   * Ответ на предложение расширить географию (FE-390, MVP_PRD §43.3).
   *
   * Согласие уводит на экран результатов — там считается новый прогон той же
   * версии критериев, бесплатно и внутри того же заказа. Отказ тоже уходит на
   * сервер: он фиксируется, и вопрос не повторяется до следующей версии
   * критериев. Прятать блок локально нельзя — после перезагрузки он вернулся бы.
   */
  const answerGeoOffer = useCallback(
    (accepted: boolean) => {
      if (!sessionId || geoOffer === null) return;
      geoExpansion.mutate(
        { sessionId, accepted, step: geoOffer.next_step },
        {
          onSuccess: (result) => {
            if (result.search_run_id !== null) openRun(result.search_run_id);
          },
        },
      );
    },
    [geoExpansion, geoOffer, openRun, sessionId],
  );

  if (state === null) {
    return (
      <div className={styles.loading} aria-hidden="true">
        <Skeleton height="1rem" width="70%" />
        <Skeleton height="4rem" radius="var(--radius-card)" />
      </div>
    );
  }

  return (
    <div className={styles.dialogue}>
      <ChatPanel
        variant="rail"
        session={state}
        draft={draft}
        maxLength={maxLength}
        sending={sendMessage.isPending}
        sendError={sendMessage.error}
        onSend={send}
      />

      {state.rebuild_pending ? (
        // `role="status"`: предложение появляется в ответ на сообщение, и
        // скринридер обязан его получить, не отбирая фокус у поля ввода.
        <div className={styles.rebuild} role="status">
          <p className={styles.rebuildText}>{t('chat.rail.rebuild.question')}</p>
          <p className={styles.rebuildNote}>{t('chat.rail.rebuild.note')}</p>
          {/* Действие внутри диалога — синий контур: красная в рельсе только
              отправка сообщения (DESIGN_SYSTEM §5.1). */}
          <Button
            variant="outline-action"
            fullWidth
            disabled={confirmSearch.isPending}
            onClick={rebuild}
            className={styles.action}
          >
            {confirmSearch.isPending
              ? t('chat.rail.rebuild.pending')
              : t('chat.rail.rebuild.action')}
          </Button>
        </div>
      ) : null}

      {geoOffer !== null ? (
        // Числа — из ответа сервера: «в вашей земле 12, соседние добавят 34».
        // Литералов в этом тексте нет и быть не может (CLAUDE.md §2).
        <div className={styles.rebuild} role="status">
          <p className={styles.rebuildText}>
            {t(`chat.rail.geoExpansion.question.${geoOffer.next_step_code}`, {
              count: geoOffer.would_add,
              current: geoOffer.current_count,
            })}
          </p>
          <p className={styles.rebuildNote}>{t('chat.rail.geoExpansion.note')}</p>
          <div className={styles.geoActions}>
            <Button
              variant="outline-action"
              disabled={geoExpansion.isPending}
              className={styles.action}
              onClick={() => {
                answerGeoOffer(true);
              }}
            >
              {geoExpansion.isPending
                ? t('chat.rail.geoExpansion.pending')
                : t('chat.rail.geoExpansion.accept')}
            </Button>
            <Button
              variant="secondary"
              disabled={geoExpansion.isPending}
              className={styles.action}
              onClick={() => {
                answerGeoOffer(false);
              }}
            >
              {t('chat.rail.geoExpansion.decline')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
